import * as Sentry from '@sentry/react';
import { ValidationError } from '@/app/errors/validation-error';
import { PaymentError } from '@/app/errors/payment-error';
import envService from './env.service';

class SentryService {
  private initialized = false;

  init(): void {
    const env = envService.getEnv();

    if (env.VITE_DEPLOYMENT_TARGET === 'LOCAL') {
      console.log('Sentry disabled for LOCAL environment');
      return;
    }

    if (this.initialized) {
      console.warn('Sentry already initialized');
      return;
    }

    Sentry.init({
      dsn: env.VITE_SENTRY_DSN,
      environment: env.VITE_SENTRY_ENVIRONMENT || env.VITE_DEPLOYMENT_TARGET,
      release: env.VITE_VERSION,

      integrations: [
        Sentry.browserTracingIntegration(),
        // Note: Session replay disabled per user preference
      ],

      // 25% sampling in production, 100% in development
      tracesSampleRate: env.VITE_DEPLOYMENT_TARGET === 'PROD' ? 0.25 : 1.0,

      // Filter expected errors
      beforeSend(event, hint) {
        const error = hint.originalException;
        if (error instanceof ValidationError || error instanceof PaymentError) {
          return null; // Don't send to Sentry
        }
        return event;
      },

      ignoreErrors: [
        /extensions\//i,
        /^Non-Error promise rejection captured/,
        'Network request failed',
        'Failed to fetch',
      ],
    });

    this.initialized = true;
    console.log('Sentry initialized:', {
      environment: env.VITE_SENTRY_ENVIRONMENT,
      release: env.VITE_VERSION,
    });
  }

  setUser(userId: string): void {
    if (!this.initialized) return;
    Sentry.setUser({ id: userId });
  }

  clearUser(): void {
    if (!this.initialized) return;
    Sentry.setUser(null);
  }

  captureException(error: Error, context?: Record<string, any>): void {
    if (!this.initialized) return;
    Sentry.captureException(error, { extra: context });
  }

  captureMessage(message: string, level: 'info' | 'warning' | 'error' = 'info'): void {
    if (!this.initialized) return;
    Sentry.captureMessage(message, level);
  }

  addBreadcrumb(breadcrumb: {
    message: string;
    category?: string;
    level?: Sentry.SeverityLevel;
    data?: any;
  }): void {
    if (!this.initialized) return;
    Sentry.addBreadcrumb(breadcrumb);
  }
}

export default new SentryService();
